<template>
  <v-autocomplete
    outlined
    v-model="sources"
    :items="sourceList"
    :menu-props="{ maxHeight: '400', zIndex: zindex }"
    :label="label"
    :multiple="multiple"
    small-chips
    deletable-chips
    item-text="name"
    item-value="id"
    hide-details
    :dense="dense"
  />
</template>

<script>
import compact from "lodash/compact";
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";

import { computed, onMounted, reactive, set } from "@vue/composition-api";

import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";

const SOURCE_DISPLAYABLE_TEXTS = {
  Concurrency: "Sites concurrents",
  Gtrend: "Google Trends",
  GoogleNews: "Google News",
};

export default {
  name: "SourceFilter",
  components: {},
  props: {
    initialValue: {
      type: Number | Array,
      validator(values) {
        if (isArray(values)) {
          return (
            values.length === 0 ||
            values.every((value) => typeof value === "number")
          );
        }

        return !isNaN(values);
      },
    },
    exclude: {
      type: Array,
      default: () => [],
      validator(values) {
        return (
          isArray(values) && values.every((value) => typeof value === "string")
        );
      },
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    noUrl: {
      type: Boolean,
      default: false,
    },
    store: {
      type: String,
    },
    storeGetter: {
      type: String,
      default: "getSources",
    },
    storeUpdater: {
      type: String,
      default: "updateSources",
    },
    zindex: {
      type: Number,
      default: 13,
    },
  },
  setup(props, { emit }) {
    const { store } = useProxy();

    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const page = reactive({
      isInitialized: false,
      initialUrlValue: props.multiple ? [] : "", // in case we need to load from url params
    });

    // =>  METHODS

    // =>  COMPUTED
    const label = computed(() => (props.multiple ? "Sources" : "Source"));
    const viaStore = computed(() => props.store !== undefined);
    const initialValue = computed(() =>
      props.noUrl
        ? props.initialValue
        : page.initialUrlValue ?? props.initialValue
    );

    const sourceList = computed(() => {
      const list = store.getters["newsRadar/getSourceList"].map((item) => {
        if (item.name in SOURCE_DISPLAYABLE_TEXTS) {
          return {
            ...item,
            name: SOURCE_DISPLAYABLE_TEXTS[item.name],
          };
        }

        return item;
      });

      return props.exclude.length > 0
        ? compact(
            list.map((source) =>
              props.exclude.includes(source.name) ? undefined : source
            )
          )
        : list;
    });

    const sources = computed({
      get: () => {
        if (viaStore.value) {
          return store.getters[`${props.store}/${props.storeGetter}`];
        }

        return initialValue.value;
      },
      set: (value) => {
        if (viaStore.value) {
          store.dispatch(`${props.store}/${props.storeUpdater}`, value);

          if (page.isInitialized && !props.noUrl) {
            // Only update user changes, not initialization
            addQueryStringParam({
              param: "sources",
              value: value,
              is_multiple: props.multiple,
              is_integer: true,
              // is_boolean: false,
            });
          }
        }

        if (!isEqual(value, initialValue.value)) {
          emit(
            "valueChanged",
            props.multiple
              ? compact(
                  sourceList.value.map((s) =>
                    value.includes(s.id) ? s : undefined
                  )
                )
              : sourceList.value.find((s) => s.id === value)
          );
        }
      },
    });

    // =>  WATCH

    // =>  HOOKS
    onMounted(async () => {
      await store.dispatch("newsRadar/getSourceList");
      if (viaStore.value && !props.noUrl) {
        set(
          page,
          "initialUrlValue",
          await synchronizeFilterWithQueryString({
            dispatcher: viaStore.value
              ? `${props.store}/${props.storeUpdater}`
              : "",
            param: "sources",
            value: sources.value,
            is_multiple: props.multiple,
            is_integer: true,
            // is_boolean: false,
            // dependsOn: undefined,
          }),
        );
      }

      page.isInitialized = true;
    });

    // =>  DATA
    return {
      label,
      sources,
      sourceList,
    };
  },
};
</script>

<style></style>
